<template>
    <v-app>
        <v-container fluid>
            <v-row>
                <v-col cols="12">

                    <v-card :loading="loading" :disabled="loading">
                        <v-card-title class="text-h5 pa-4">
                            <v-icon class="me-2">mdi-account-edit</v-icon>

                            Employee Details - {{ employeeData?.name || 'Loading...' }}
                            <v-progress-circular v-if="loading" indeterminate size="20" class="ml-2"
                                color="primary"></v-progress-circular>
                        </v-card-title>

                        <v-card-text class="pa-0">
                            <v-row no-gutters>
                                <!-- Left Sidebar with Tabs -->
                                <v-col cols="12" md="3" class="border-e">
                                    <v-list density="compact" class="pa-0">
                                        <v-list-item v-for="tab in tabs" :key="tab.value" :value="tab.value"
                                            :active="activeTab === tab.value" @click="handleTabChange(tab.value)"
                                            class="tab-item" :class="{ 'tab-active': activeTab === tab.value }">
                                            <template v-slot:prepend>
                                                <v-icon :color="activeTab === tab.value ? 'primary' : 'grey'">
                                                    {{ tab.icon }}
                                                </v-icon>
                                            </template>
                                            <v-list-item-title class="text-caption">
                                                {{ tab.title }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-col>

                                <!-- Right Content Area -->
                                <v-col cols="12" md="9" class="pa-4">
                                    <!-- Personal Information Tab Content -->
                                    <PersonalInformation v-if="activeTab === 'personal'"
                                        :personal-info="employeeData"
                                        :genders="genders"
                                        :blood-groups="bloodGroups"
                                        :religions="religions"
                                        :marital-statuses="maritalStatuses"
                                        :loading-states="{ genders: false, bloodGroups: false, religions: false, maritalStatuses: false }" />

                                    <!-- General Information Tab Content -->
                                    <GeneralInformation v-if="activeTab === 'general'"
                                        v-model:official-info="officialInfo"
                                        v-model:bank-info="bankInfo"
                                        v-model:tax-info="taxInfo"
                                        v-model:driving-info="drivingInfo"
                                        v-model:employment-status="employmentStatus"
                                        :employee-types="employeeTypes" 
                                        :payment="payment"
                                        :departments="departments" 
                                        :sections="sections"
                                        :designations="designations"
                                        :reason="getHrmPaymentReasonList" 
                                        :banks="banks" 
                                        :loading-states="loadingStates" />

                                    <!-- Salary Information Tab Content -->
                                    <SalaryInformation v-if="activeTab === 'salary'" v-model:salary-info="salaryInfo"
                                        :employee-id="employeeId" />

                                    <!-- Settings Information Tab Content -->
                                    <SettingsInformation v-if="activeTab === 'settings'"
                                        v-model:settings-info="settingsInfo" :employee-id="employeeId" />

                                    <!-- Educational Information Tab Content -->
                                    <EducationalInformationNew v-if="activeTab === 'educational'"
                                        :employee-id="employeeId" ref="educationalInfoRef" />

                                    <!-- Employee Address Tab Content -->
                                    <AddressInformation v-if="activeTab === 'address'"
                                        ref="addressInfoRef"
                                        v-model:address-info="addressInfo" :employee-id="employeeId" />

                                    <!-- Previous Employment Tab Content -->
                                    <PreviousEmployment v-if="activeTab === 'previous-employment'"
                                        ref="previousEmploymentRef"
                                        :previous-employment="previousEmployment" :employee-id="employeeId"
                                        :employee-joining-date="officialInfo.joining_date" />

                                    <!-- Other Information Tab Content -->
                                    <OtherInformation v-if="activeTab === 'other'" ref="otherInfoRef"
                                        v-model:other-info="otherInfo" :employee-id="employeeId" />
                                </v-col>
                            </v-row>
                        </v-card-text>

                        <v-card-actions class="pa-4">
                            <v-spacer></v-spacer>
                           
                            <v-btn color="primary" variant="flat" @click="saveEmployeeDetails" :loading="saving">
                                <v-icon class="me-2">mdi-content-save</v-icon>
                                Save Changes
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>
    </v-app>
</template>

<script>
import { toast } from 'vue3-toastify';
import PersonalInformation from '@/components/module/hrm/employeeDetails/PersonalInformation.vue';
import GeneralInformation from '@/components/module/hrm/employeeDetails/GeneralInformation.vue';
import SalaryInformation from '@/components/module/hrm/employeeDetails/SalaryInformation.vue';
import SettingsInformation from '@/components/module/hrm/employeeDetails/SettingsInformation.vue';
import EducationalInformationNew from '@/components/module/hrm/employeeDetails/EducationalInformation.vue';
import AddressInformation from '@/components/module/hrm/employeeDetails/AddressInformation.vue';
import PreviousEmployment from '@/components/module/hrm/employeeDetails/PreviousEmployment.vue';
import OtherInformation from '@/components/module/hrm/employeeDetails/OtherInformation.vue';


export default {
    name: 'HrmEmployeeDetailsRefactored',
    components: {
        PersonalInformation,
        GeneralInformation,
        SalaryInformation,
        SettingsInformation,
        EducationalInformationNew,
        AddressInformation,
        PreviousEmployment,
        OtherInformation,
    },
    data() {
        return {
            activeTab: 'personal',
            employeeId: null,
            employeeData: {},
            departments: [],
            sections: [],
            designations: [],
            employeeTypes: [],
            payment: [
                { label: "Paid", value: 1 },
                { label: "Unpaid", value: 2 }
            ],
            bloodGroups: [],
            genders: [],
            religions: [],
            maritalStatuses: [],
            banks: [],
            
            loading: false,
            personalLoading: false,
            generalLoading: false,
            generalDataLoaded: false,  // Track if general data has been loaded
            salaryLoading: false,
            salaryDataLoaded: false,  // Track if salary data has been loaded
            settingsLoading: false,
            settingsDataLoaded: false,  // Track if settings data has been loaded
            educationalDataLoaded: false,  // Track if educational data has been loaded
            addressDataLoaded: false,  // Track if address data has been loaded
            previousEmploymentDataLoaded: false,  // Track if previous employment data has been loaded
            otherDataLoaded: false,  // Track if other data has been loaded
            loadingStates: {
                departments: false,
                sections: false,
                designations: false,
                employeeTypes: false,
                banks: false,
                bloodGroups: false,
                genders: false,
                religions: false,
                maritalStatuses: false
            },
            educationalLoading: false,
            addressLoading: false,
            previousEmploymentLoading: false,
            otherLoading: false,
            employeeName: 'Loading...',
            saving: false,
            personalInfo: {},
            officialInfo: {
                code: '',
                status: '',
                employee_type_id: '',
                department_id: '',
                section: '',
                designation_id: '',
                phone_office: '',
                phone_residence: '',
                employee_category: '',
                office_location: '',
                appointment_date: '',
                confirmation_date: '',
                joining_date: '',
                late_count: '',
                hod_approve_employee_id: null,
                hr_approve_employee_id: null
            },
            bankInfo: {
                bank_id: '',
                account_number: ''
            },
            taxInfo: {
                tin_number: '',
                tax_circle: '',
                tax_zone: ''
            },
            drivingInfo: {
                license_number: '',
                renewal_from: '',
                renewal_to: ''
            },
            employmentStatus: {
                payment: '',
                reason_id: null,
                leaving_date: null,
                note: ''
            },
            salaryInfo: {},
            settingsInfo: {
                latitude: '',
                longitude: '',
                ot_applicable: '',
                daily_ot_type: '',
                daily_ot_rate: '',
                daily_ot_pay_on: '',
                daily_ot_amount: '',
                hourly_ot_type: '',
                hourly_ot_rate: '',
                hourly_ot_rate_percentage: '',
                hourly_ot_pay_on: '',
                hourly_ot_amount: ''
            },
            addressInfo: {},
            previousEmployment: {},
            otherInfo: {},
            yearOptions: [],
            boardOptions: [],
            examinationOptions: [],
            resultOptions: [],
            tabs: [
                { value: 'personal', title: 'Personal Information', icon: 'mdi-account' },
                { value: 'general', title: 'General Information', icon: 'mdi-information' },
                { value: 'salary', title: 'Salary Information', icon: 'mdi-cash' },
                { value: 'settings', title: 'Settings Information', icon: 'mdi-cog' },
                { value: 'educational', title: 'Educational Information', icon: 'mdi-school' },
                { value: 'address', title: 'Employee Address', icon: 'mdi-map-marker' },
                { value: 'previous-employment', title: 'Previous Employment', icon: 'mdi-briefcase-outline' },
                { value: 'other', title: 'Other Information', icon: 'mdi-account-details' }
            ]
        };
    },
    computed: {
        getEmployeeDetails() {
            return this.$store.getters.getEmployeeDetails || {};
        },
        getEmployeeGeneralInfo() {
            return this.$store.getters.getEmployeeGeneralInfo || {};
        },
        getHrmPaymentReasonList() {
            return this.$store.getters.getHrmPaymentReasonList || [];
        },
    },
    watch: {
        activeTab(newVal) {
            // Start with personal tab logic
            if (newVal === 'personal' && Object.keys(this.employeeData).length === 0) {
                this.loadPersonalData();
            }
            // Add general tab logic
            if (newVal === 'general' && !this.generalDataLoaded) {
                this.loadGeneralData();
            }
            // Add salary tab logic
            if (newVal === 'salary' && !this.salaryDataLoaded) {
                this.loadSalaryData();
            }
            // Add settings tab logic
            if (newVal === 'settings' && !this.settingsDataLoaded) {
                this.loadSettingsData();
            }
            // Add educational tab logic
            if (newVal === 'educational' && !this.educationalDataLoaded) {
                this.educationalDataLoaded = true; // Mark as loaded since component handles its own loading
            }
            // Add address tab logic
            if (newVal === 'address' && !this.addressDataLoaded) {
                this.addressDataLoaded = true; // Mark as loaded since component handles its own loading
            }
            // Add previous-employment tab logic
            if (newVal === 'previous-employment' && !this.previousEmploymentDataLoaded) {
                this.previousEmploymentDataLoaded = true; // Mark as loaded since component handles its own loading
            }
            // Add other tab logic
            if (newVal === 'other' && !this.otherDataLoaded) {
                this.otherDataLoaded = true; // Mark as loaded since component handles its own loading
            }
        }
    },
    created() {
        // Load initial data (like asset page)
        this.employeeId = this.$route.params.id;
        this.initializeStaticOptions();
        this.loadPersonalData();  // Load personal initially
        this.loadDropdownData();  // Personal dropdowns
    },
    beforeUnmount() {
        // Cleanup (like asset page)
        const unsetMutations = [
            // Add mutations like 'UNSET_EMPLOYEE_DETAILS'
        ];
        unsetMutations.forEach(mutation => {
            this.$store.commit(mutation);
        });
    },
    methods: {
        initializeStaticOptions() {},

        async loadDropdownData() {
            // Only use API/store data, no fallback
            await Promise.all([
                this.$store.dispatch('getHrmGenderList'),
                this.$store.dispatch('getHrmBloodGroupList'),
                this.$store.dispatch('getHrmReligionList'),
                this.$store.dispatch('getHrmMaritalStatusList')
            ]);
            const genders = this.$store.getters.getHrmGenderList || [];
            const bloodGroups = this.$store.getters.getHrmBloodGroupList || [];
            const religions = this.$store.getters.getHrmReligionList || [];
            const maritalStatuses = this.$store.getters.getHrmMaritalStatusList || [];
            this.genders = genders.map(g => ({ text: g.name, value: g.id }));
            this.bloodGroups = bloodGroups.map(b => ({ text: b.name, value: b.id }));
            this.religions = religions.map(r => ({ text: r.name, value: r.id }));
            this.maritalStatuses = maritalStatuses.map(m => ({ text: m.name, value: m.id }));
        },
        
        async loadGeneralDropdowns() {
            try {
                await Promise.all([
                    this.$store.dispatch('getHrmDepartmentList'),
                    this.$store.dispatch('getHrmSectionList'),
                    this.$store.dispatch('getHrmDesignationList'),
                    this.$store.dispatch('getHrmEmployeeTypeList'),
                    this.$store.dispatch('getHrmEmployeeBankSetupList'),
                    this.$store.dispatch('getHrmPaymentReasonList'),
                ]);
                
                this.departments = this.$store.getters.getHrmDepartmentList.map(dept => ({
                    text: dept.name,
                    value: dept.id
                }));
                this.sections = this.$store.getters.getHrmSectionList.map(section => ({
                    text: section.name,
                    value: section.id
                }));
                this.designations = this.$store.getters.getHrmDesignationList.map(designation => ({
                    text: designation.name,
                    value: designation.id
                }));
                this.employeeTypes = this.$store.getters.getHrmEmployeeTypeList.map(type => ({
                    text: type.name,
                    value: type.id
                }));
                this.banks = this.$store.getters.getHrmEmployeeBankSetupList.map(bank => ({
                    text: bank.name,
                    value: bank.id
                }));
            } catch (error) {
                console.error('Error loading general dropdowns:', error);
            }
        },
        
        async loadPersonalData() {
            this.personalLoading = true;
            try {
                this.loading=true;
                await this.$store.dispatch('getEmployeeDetails', this.employeeId)
                    .then(()=> this.loading = false);
                const employee = this.getEmployeeDetails;
                if (employee) {
                    this.employeeData = {
                        name: employee.name || '',
                        name_bangla: employee.name_bangla || '',
                        father_name: employee.father_name || '',
                        mother_name: employee.mother_name || '',
                        mobile: employee.mobile || '',
                        email: employee.email || '',
                        nid: employee.nid || '',
                        passport_no: employee.passport_no || '',
                        date_of_birth: employee.date_of_birth || '',
                        nationality: employee.nationality || '',
                        height: employee.height || '',
                        weight: employee.weight || '',
                        gender_id: employee.gender_id || '',
                        blood_group_id: employee.blood_group_id || '',
                        religion_id: employee.religion_id || '',
                        marital_status_id: employee.marital_status_id || '',
                        emergency_contact_person: employee.emergency_contact_person || '',
                        emergency_contact_no: employee.emergency_contact_no || ''
                    };
                }
            } catch (error) {
                console.error('Error loading personal data:', error);
            } finally {
                this.personalLoading = false;
            }
        },
        
        async loadGeneralData() {
            this.generalLoading = true;
            try {
                this.loading = true;

                await this.loadGeneralDropdowns();
                await this.$store.dispatch('getEmployeeGeneralInfo', this.employeeId);

                const generalData = this.getEmployeeGeneralInfo;
                if (generalData) {
                    // Populate official info
                    this.officialInfo = {
                        code: generalData.employee?.code || '',
                        status: generalData.employee?.status || '',
                        employee_type_id: generalData.employee?.employee_type_id || '',
                        department_id: generalData.employee?.department_id || '',
                        section: generalData.employee?.section_id || '',
                        designation_id: generalData.employee?.designation_id || '',
                        phone_office: generalData.employee?.phone_office || '',
                        phone_residence: generalData.employee?.phone_residence || '',
                        employee_category: generalData.general_info?.employee_category || '',
                        office_location: generalData.general_info?.office_location || '',
                        appointment_date: generalData.general_info?.appointment_date || '',
                        confirmation_date: generalData.general_info?.confirmation_date || '',
                        joining_date: generalData.employee?.joining_date || '',
                        late_count: generalData.general_info?.late_count || '',
                        hod_approve_employee_id: generalData.employee?.hod_approve_employee_id || null,
                        hr_approve_employee_id: generalData.employee?.hr_approve_employee_id || null,

                    };
                    
                    // Populate bank info
                    if (generalData.bank_info) {
                        this.bankInfo = {
                            bank_id: generalData.bank_info.bank?.id || '',
                            account_number: generalData.bank_info.account_number || ''
                        };
                    }
                    
                    // Populate tax info
                    if (generalData.tax_info) {
                        this.taxInfo = {
                            tin_number: generalData.tax_info.tin_number || '',
                            tax_circle: generalData.tax_info.tax_circle || '',
                            tax_zone: generalData.tax_info.tax_zone || ''
                        };
                    }
                    
                    // Populate driving info
                    if (generalData.driving_info) {
                        this.drivingInfo = {
                            license_number: generalData.driving_info.license_number || '',
                            renewal_from: generalData.driving_info.renewal_from || '',
                            renewal_to: generalData.driving_info.renewal_to || ''
                        };
                    }

                    // Populate employment status
                    if (generalData.employment_status) {
                        this.employmentStatus = {
                            payment: generalData.employment_status.payment || '',
                            reason_id: generalData.employment_status.reason_id || null,
                            leaving_date: generalData.employment_status.leaving_date || null,
                            note: generalData.employment_status.note || ''
                        };
                    }
                }
                this.generalDataLoaded = true;
            } catch (error) {
                console.error('Error loading general data:', error);
            } finally {
                this.loading = false;
                this.generalLoading = false;
            }
        },
        
        async loadSalaryData() {
            this.salaryLoading = true;
            try {
                this.loading= true;
                const response = await this.$store.dispatch('getSalaryInfoByEmployee', this.employeeId)
                    .then((res) => {
                        this.loading = false;
                        return res;
                    });

                if (response && response.data && response.data.success && response.data.data && response.data.data.length > 0) {
                    const latestSalaryInfo = response.data.data[0];
                    //console.log(latestSalaryInfo);
                    this.salaryInfo = {
                        salary_info_id: latestSalaryInfo.id,
                        effect_date: latestSalaryInfo.effect_date ? new Date(latestSalaryInfo.effect_date).toISOString().split('T')[0] : '',
                        package: latestSalaryInfo.package || '',
                        gross_distribution_status: latestSalaryInfo.gross_distribution_status,
                        gross_salary: latestSalaryInfo.gross_salary || 0,
                        basic_salary: latestSalaryInfo.basic_salary || 0,
                        house_rent: latestSalaryInfo.house_rent || 0,
                        mobile: latestSalaryInfo.mobile || 0,
                        medical: latestSalaryInfo.medical || 0,
                        conveyance: latestSalaryInfo.conveyance || 0,
                        education: latestSalaryInfo.education || 0,
                        transport: latestSalaryInfo.transport || 0,
                        technical: latestSalaryInfo.technical || 0,
                        festival_allowance: latestSalaryInfo.festival_allowance || 0,
                        tiffin: latestSalaryInfo.tiffin || 0,
                        incentive: latestSalaryInfo.incentive || 0,
                        wash: latestSalaryInfo.wash || 0,
                        others: latestSalaryInfo.others || 0,
                        tax_deducted: latestSalaryInfo.tax_deducted || 0,
                        car_use: latestSalaryInfo.car_use || 0,
                        pf_provident_fund: latestSalaryInfo.pf_provident_fund || 0,
                        gas_bill: latestSalaryInfo.gas_bill || 0,
                        electricity_bill: latestSalaryInfo.electricity_bill || 0,
                        telephone_bill: latestSalaryInfo.telephone_bill || 0,
                        insurance: latestSalaryInfo.insurance || 0,
                        others_deducted: latestSalaryInfo.others_deducted || 0
                    };
                } else {
                    // Initialize with empty values if no data found
                    this.salaryInfo = {
                        effect_date: '',
                        package: '',
                        gross_distribution_status: true,
                        gross_salary: 0,
                        basic_salary: 0,
                        house_rent: 0,
                        mobile: 0,
                        medical: 0,
                        conveyance: 0,
                        education: 0,
                        transport: 0,
                        technical: 0,
                        festival_allowance: 0,
                        tiffin: 0,
                        incentive: 0,
                        wash: 0,
                        others: 0,
                        tax_deducted: 0,
                        car_use: 0,
                        pf_provident_fund: 0,
                        gas_bill: 0,
                        electricity_bill: 0,
                        telephone_bill: 0,
                        insurance: 0,
                        others_deducted: 0
                    };
                }
                this.salaryDataLoaded = true;
            } catch (error) {
                console.error('Error loading salary data:', error);
                // Initialize with empty values on error
                this.salaryInfo = {
                    effect_date: '',
                    package: '',
                    gross_distribution_status: false,
                    gross_salary: 0,
                    basic_salary: 0,
                    house_rent: 0,
                    mobile: 0,
                    medical: 0,
                    conveyance: 0,
                    education: 0,
                    transport: 0,
                    technical: 0,
                    festival_allowance: 0,
                    tiffin: 0,
                    incentive: 0,
                    wash: 0,
                    others: 0,
                    tax_deducted: 0,
                    car_use: 0,
                    pf_provident_fund: 0,
                    gas_bill: 0,
                    electricity_bill: 0,
                    telephone_bill: 0,
                    insurance: 0,
                    others_deducted: 0
                };
            } finally {
                this.salaryLoading = false;
            }
        },
        
        async loadSettingsData() {
            this.settingsLoading = true;
            try {
                const response = await this.$store.dispatch('settingsInfo/getSettingsInfoByEmployee', this.employeeId);
                if (response?.data?.success && response?.data?.data) {
                    const settingsData = response.data.data;
                    this.settingsInfo = {
                        longitude: settingsData?.employee?.longitude || '',
                        latitude: settingsData?.employee?.latitude || '',
                        ot_applicable: settingsData.ot_applicable || '',
                        daily_ot_type: settingsData.daily_ot_type || '',
                        daily_ot_rate: settingsData.daily_ot_rate || '',
                        daily_ot_pay_on: settingsData.daily_ot_pay_on || '',
                        daily_ot_amount: settingsData.daily_ot_amount || '',
                        hourly_ot_type: settingsData.hourly_ot_type || '',
                        hourly_ot_rate: settingsData.hourly_ot_rate || '',
                        hourly_ot_rate_percentage: settingsData.hourly_ot_rate_percentage || '',
                        hourly_ot_pay_on: settingsData.hourly_ot_pay_on || '',
                        hourly_ot_amount: settingsData.hourly_ot_amount || ''
                    };
                } else {
                    // Initialize with empty values if no data found
                    this.settingsInfo = {
                        longitude: '',
                        latitude: '',
                        ot_applicable: '',
                        daily_ot_type: '',
                        daily_ot_rate: '',
                        daily_ot_pay_on: '',
                        daily_ot_amount: '',
                        hourly_ot_type: '',
                        hourly_ot_rate: '',
                        hourly_ot_rate_percentage: '',
                        hourly_ot_pay_on: '',
                        hourly_ot_amount: ''
                    };
                }
                this.settingsDataLoaded = true;
            } catch (error) {
                console.error('Error loading settings data:', error);
                // Initialize with empty values on error
                this.settingsInfo = {
                    longitude: '',
                    latitude: '',
                    ot_applicable: '',
                    daily_ot_type: '',
                    daily_ot_rate: '',
                    daily_ot_pay_on: '',
                    daily_ot_amount: '',
                    hourly_ot_type: '',
                    hourly_ot_rate: '',
                    hourly_ot_rate_percentage: '',
                    hourly_ot_pay_on: '',
                    hourly_ot_amount: ''
                };
            } finally {
                this.settingsLoading = false;
            }
        },
        
        async saveEmployeeDetails() {
            try {
                this.saving = true;
                
                if (this.activeTab === 'personal') {
                    await this.savePersonalInfo();
                } else if (this.activeTab === 'general') {
                    await this.saveGeneralInfo();
                } else if (this.activeTab === 'salary') {
                    await this.saveSalaryInfo();
                } else if (this.activeTab === 'settings') {
                    await this.saveSettingsInfo();
                } else if (this.activeTab === 'educational') {
                    await this.saveEducationalInfo();
                } else if (this.activeTab === 'address') {
                    await this.saveAddressInfo();
                } else if (this.activeTab === 'previous-employment') {
                    await this.savePreviousEmploymentInfo();
                } else if (this.activeTab === 'other') {
                    await this.saveOtherInfo();
                }
                
                // Success messages are handled by individual child components
            } catch (error) {
                console.error('Error saving employee data:', error);
                const msg = error.response?.data?.message || error.message;
                toast.error('Failed to save employee details: ' + msg);
            } finally {
                this.saving = false;
            }
        },
        
        async savePersonalInfo() {
    const employee = this.getEmployeeDetails;
    if (!employee) {
        toast.error('Employee data not loaded. Please refresh and try again.');
        return;
    }
    const personalInfoData = {
        employee_id: this.employeeId,
        
        name: this.employeeData.name,
        name_bangla: this.employeeData.name_bangla,
        father_name: this.employeeData.father_name,
        mother_name: this.employeeData.mother_name,
        mobile: this.employeeData.mobile,
        email: this.employeeData.email,
        nid: this.employeeData.nid,
        passport_no: this.employeeData.passport_no,
        date_of_birth: this.employeeData.date_of_birth,
        nationality: this.employeeData.nationality,
        height: this.employeeData.height,
        weight: this.employeeData.weight,
        gender_id: this.employeeData.gender_id,
        blood_group_id: this.employeeData.blood_group_id,
        religion_id: this.employeeData.religion_id,
        marital_status_id: this.employeeData.marital_status_id,
        emergency_contact_person: this.employeeData.emergency_contact_person,
        emergency_contact_no: this.employeeData.emergency_contact_no,
        
        code: employee.code,
        branch_id: employee.branch_id,
        department_id: employee.department_id,
        designation_id: employee.designation_id,
        section_id: employee.section_id ?? this.officialInfo.section ?? '',
        employee_type_id: employee.employee_type_id,
        status: employee.status,
        joining_date: employee.joining_date ?? this.officialInfo.joining_date ?? '',
        phone_residence: employee.phone_residence ?? this.officialInfo.phone_residence ?? '',
        phone_office: employee.phone_office ?? this.officialInfo.phone_office ?? '',
        permanent_address: employee.permanent_address ?? '',
        user_id: employee.user_id ?? null,
        hod_approve_employee_id: employee.hod_approve_employee_id ?? this.officialInfo.hod_approve_employee_id ?? null,
        hr_approve_employee_id: employee.hr_approve_employee_id ?? this.officialInfo.hr_approve_employee_id ?? null
    };
    await this.$store.dispatch('saveUpdateEmployeeSetup', personalInfoData);
    toast.success('Personal information saved successfully');
},
        
async saveGeneralInfo() {
    const generalInfoData = {
        employee_id: this.employeeId,
        
        employee: {
            code: this.officialInfo.code,
            employee_type_id: this.officialInfo.employee_type_id,
            department_id: this.officialInfo.department_id,
            designation_id: this.officialInfo.designation_id,
            section_id: this.officialInfo.section || this.officialInfo.section_id || null,
            status: this.officialInfo.status,
            phone_office: this.officialInfo.phone_office,
            phone_residence: this.officialInfo.phone_residence,
            joining_date: this.officialInfo.joining_date,
            hod_approve_employee_id: this.officialInfo.hod_approve_employee_id,
            hr_approve_employee_id: this.officialInfo.hr_approve_employee_id
        },
        general_info: {
            employee_category: this.officialInfo.employee_category,
            office_location: this.officialInfo.office_location,
            appointment_date: this.officialInfo.appointment_date,
            confirmation_date: this.officialInfo.confirmation_date,
            joining_date: this.officialInfo.joining_date,
            late_count: this.officialInfo.late_count
        },
        bank_info: {
            bank_id: this.bankInfo.bank_id,
            account_number: this.bankInfo.account_number
        },
        tax_info: {
            tin_number: this.taxInfo.tin_number,
            tax_circle: this.taxInfo.tax_circle,
            tax_zone: this.taxInfo.tax_zone
        },
        driving_info: {
            license_number: this.drivingInfo.license_number,
            renewal_from: this.drivingInfo.renewal_from,
            renewal_to: this.drivingInfo.renewal_to
        },
        employment_status: {
            payment: this.employmentStatus.payment,
            reason_id: this.employmentStatus.reason_id,
            leaving_date: this.employmentStatus.leaving_date,
            note: this.employmentStatus.note,
        }
    };

    await this.$store.dispatch('saveEmployeeGeneralInfo', generalInfoData);
    toast.success('General information saved successfully');
},
        
        async saveSalaryInfo() {
            const salaryData = {
                salary_info_id: this.salaryInfo.salary_info_id || null,
                employee_id: this.employeeId,
                effect_date: this.salaryInfo.effect_date,
                package: this.salaryInfo.package,
                gross_distribution_status: this.salaryInfo.gross_distribution_status,
                gross_salary: this.salaryInfo.gross_salary || 0,
                basic_salary: this.salaryInfo.basic_salary || 0,
                house_rent: this.salaryInfo.house_rent || 0,
                mobile: this.salaryInfo.mobile || 0,
                medical: this.salaryInfo.medical || 0,
                conveyance: this.salaryInfo.conveyance || 0,
                education: this.salaryInfo.education || 0,
                transport: this.salaryInfo.transport || 0,
                technical: this.salaryInfo.technical || 0,
                festival_allowance: this.salaryInfo.festival_allowance || 0,
                tiffin: this.salaryInfo.tiffin || 0,
                incentive: this.salaryInfo.incentive || 0,
                wash: this.salaryInfo.wash || 0,
                others: this.salaryInfo.others || 0,
                tax_deducted: this.salaryInfo.tax_deducted || 0,
                car_use: this.salaryInfo.car_use || 0,
                pf_provident_fund: this.salaryInfo.pf_provident_fund || 0,
                gas_bill: this.salaryInfo.gas_bill || 0,
                electricity_bill: this.salaryInfo.electricity_bill || 0,
                telephone_bill: this.salaryInfo.telephone_bill || 0,
                insurance: this.salaryInfo.insurance || 0,
                others_deducted: this.salaryInfo.others_deducted || 0
            };
            
            await this.$store.dispatch('saveSalaryInfo', salaryData);
            toast.success('Salary information saved successfully');
        },
        
        async saveSettingsInfo() {
            const settingsData = {
                employee_id: this.employeeId,
                longitude: this.settingsInfo.longitude,
                latitude: this.settingsInfo.latitude,
                ot_applicable: this.settingsInfo.ot_applicable,
                daily_ot_type: this.settingsInfo.daily_ot_type,
                daily_ot_rate: this.settingsInfo.daily_ot_rate,
                daily_ot_pay_on: this.settingsInfo.daily_ot_pay_on,
                daily_ot_amount: this.settingsInfo.daily_ot_amount,
                hourly_ot_type: this.settingsInfo.hourly_ot_type,
                hourly_ot_rate: this.settingsInfo.hourly_ot_rate,
                hourly_ot_rate_percentage: this.settingsInfo.hourly_ot_rate_percentage,
                hourly_ot_pay_on: this.settingsInfo.hourly_ot_pay_on,
                hourly_ot_amount: this.settingsInfo.hourly_ot_amount
            };
            
            await this.$store.dispatch('settingsInfo/saveSettingsInfo', settingsData);
            toast.success('Settings information saved successfully');
        },
        
        async saveEducationalInfo() {
            const ref = this.$refs.educationalInfoRef;
          if (!ref?.saveAllEducationalInfo) {
           throw new Error('Educational form is not ready. Please try again.');
    }
    await ref.saveAllEducationalInfo();
        },
        
        async saveAddressInfo() {
            const ref = this.$refs.addressInfoRef;
    if (!ref?.saveAddressInfo) {
        throw new Error('Address form is not ready. Please try again.');
    }
    await ref.saveAddressInfo();
        },
        
        async savePreviousEmploymentInfo() {
            const ref = this.$refs.previousEmploymentRef;
            if (!ref?.saveAllPreviousEmploymentInfo) {
                throw new Error('Previous employment form is not ready. Please try again.');
            }
            await ref.saveAllPreviousEmploymentInfo();
        },
        
        async saveOtherInfo() {
            const ref = this.$refs.otherInfoRef;
            if (!ref?.saveOtherInfo) {
                throw new Error('Other information form is not ready. Please try again.');
            }
            await ref.saveOtherInfo();
        },
        
        handleTabChange(tab) {
            console.log('Switching to tab:', tab); 
            this.activeTab = tab;
        },
        
        goBack() {
            this.$router.push('/hrm/employee-list');
        },
    }
};
</script>

<style scoped>
.tab-item {
    border-bottom: 1px solid #e0e0e0;
    transition: all 0.3s ease;
}

.tab-item:hover {
    background-color: #f5f5f5;
}

.tab-active {
    background-color: #e3f2fd;
    border-right: 3px solid #2196f3;
}

.border-e {
    border-right: 1px solid #e0e0e0;
}
</style>
